import { atom, useRecoilState } from "recoil";

import Environment from "services/environment";
import RestApiCall, { isRequestCancelled } from "interceptor/aeh";
import { API_FETCH_STATUS } from "constants/common";

const env = Environment.getEnv();

const { LOADING, SUCCESS, ERROR } = API_FETCH_STATUS;

const fetchSearchAPI = ({ payload }) => {
    const apiPayload = {
      method: "POST",
      url: "/user/v1/global-search/suggestedAndRecent",
      baseURL: env.feedbackBaseUrl,
      data: payload,
    };
    return RestApiCall.api(apiPayload);
};

const fetchTypeAheadSearchAPI = ({ payload, cancelToken }) => {
    const apiPayload = {
      method: "POST",
      url: "/user/v1/global-search/type-ahead",
      baseURL: env.feedbackBaseUrl,
      cancelToken,
      data: payload,
    };
    return RestApiCall.api(apiPayload);
};

const boostSearchAPI = ({ payload }) => {
    const apiPayload = {
      method: "POST",
      url: "/user/v1/global-search/boost-ranking",
      baseURL: env.feedbackBaseUrl,
      data: payload,
    };
    return RestApiCall.api(apiPayload);
};

const GLOBAL_SEARCH_INITIAL_STATE = {
    recentSearch: {
        state: null,
        contents: null,
    },
    suggestedSearch: {
        state: null,
        contents: null,
    },
    typeAheadSearch: {
        state: null,
        contents: null,
    },
};

const globalSearchAtom = atom({
    key: "globalSearch",
    default: GLOBAL_SEARCH_INITIAL_STATE,
});

const REQUEST_TYPES = {
    RECENT: "RECENT",
    SUGGESTED: "SUGGESTED",
    TYPE_AHEAD: "TYPE_AHEAD",
    RECENT_AND_SUGGESTED: "RECENT_AND_SUGGESTED",
};

const CHANNEL = "ATB";

const useGlobalSearch = () => {
    const [state, setState] = useRecoilState(globalSearchAtom);
    const { recentSearch, suggestedSearch, typeAheadSearch } = state;

    const setRecentSearch = (update) => setState((state) => ({ ...state, recentSearch: { ...update }}));
    const setSuggestedSearch = (update) => setState((state) => ({ ...state, suggestedSearch: { ...update }}));
    const setTypeAheadSearch = (update) => setState((state) => ({ ...state, typeAheadSearch: { ...update }}));

    const clearSearch = () => setState({ ...GLOBAL_SEARCH_INITIAL_STATE });

    const fetchRecentSearches = () => {
        setRecentSearch({ state: LOADING });
        const payload = { requestType: REQUEST_TYPES.RECENT, channel: CHANNEL };
        return fetchSearchAPI({ payload }).then(res => {
            const { data } = res;
            setRecentSearch({ state: SUCCESS, contents: data.recentSearch });
        }).catch(err => setRecentSearch({ state: ERROR, contents: null }));
    };

    const fetchSuggestedSearch = () => {
        setSuggestedSearch({ state: LOADING });
        const payload = { requestType: REQUEST_TYPES.SUGGESTED, channel: CHANNEL };
        return fetchSearchAPI({ payload }).then(res => {
            const { data } = res;
            setSuggestedSearch({ state: SUCCESS, contents: data.suggestedSearch });
        }).catch(err => setSuggestedSearch({ state: ERROR, contents: null }));
    };

    const fetchTypeAheadSearch = (searchQuery, cancelTokenSource) => {
        setTypeAheadSearch({ state: LOADING });
        const payload = { key: searchQuery, channel: CHANNEL };
        return fetchTypeAheadSearchAPI({ payload, cancelToken: cancelTokenSource.token }).then(res => {
            const { data } = res;
            setTypeAheadSearch({ state: SUCCESS, contents: data.typeAheadSearch });
        }).catch(err => {
            if (!isRequestCancelled(err)) {
                setTypeAheadSearch({ state: ERROR, contents: null });
            }
        });
    };

    const boostSearch = (search, type) => {
        const payload = { id: search.id, requestType: type, channel: CHANNEL };
        return boostSearchAPI({ payload });
    };

    return {
        fetchRecentSearches,
        fetchSuggestedSearch,
        fetchTypeAheadSearch,
        boostSearch,
        recentSearch,
        setRecentSearch,
        suggestedSearch,
        setSuggestedSearch,
        typeAheadSearch,
        setTypeAheadSearch,
        clearSearch,
    };
}

export default useGlobalSearch;
